import { blinkConstants } from '@thoughtspot/blink-constants';
import * as dateUtil from '@thoughtspot/date-format-util';
import { chartTypeToDatalabelFormatter, colorAxisFormatter, dataLabelFormatter, getDateTickPositioner, getTickPositionColorAxis, getUniqueTickPositionsForAxis, stackLabelFormatter, toolTipFormatter, xAxisLabelFormatter, yAxisLabelFormatter, } from '@thoughtspot/formatter-service';
import { computeAxisInfinityPlaceholderValue, getCircularReplacer, } from '@thoughtspot/js-util';
import { create } from '@thoughtspot/logger';
import { isValidNumericString } from '@thoughtspot/number-format-util';
import { isValidHexColor } from '@thoughtspot/radiant-react/widgets/color/color-utils';
import { isAccessibilityWithFormattingEnabled, isCustomCalendar, isCustomLabelProcessingDisabled, isTooltipCustomizationEnabled, } from '@thoughtspot/session-service';
import { getColumnId, getColumnIds, getId, getTimeBucket, isCalendarGregorianOrFiscal, isDateColumn, isDateFamilyColumn, isDoubleColumn, isEffectivelyNonNumeric, isFloat, isTimeColumn, } from '@thoughtspot/visualization-column-util';
import chroma from 'chroma-js';
import Highcharts from 'highcharts/es-modules/masters/highstock.src.js';
import _ from 'lodash';
import { PARETO_ID } from '../common/plugins/highcharts/pareto-chart';
import { addCortexBoundSeries } from '../components/kpi/cortex-utils';
import { getDataRowIndex, getQueryIndex } from '../core/axis-config-util';
import { getColorScaleForSerie } from '../core/chart-color-utils';
import { canShowRegressionLine, getAllAxesConfig, getAllConditionalFormattingRulesForPlotlines, getAxes, getAxisAdvancedPropertiesById, getAxisForColumn, getAxisName, getBatchSize, getCategoryColumns, getChartType, getColorBySerieName, getColumn, getColumnAdvancedPropertiesById, getHandleMissingValues, getHighchartCustomisationSettings, getLegendColumns, getRadialColumn, getSeriesColorsMap, getShowRegressionLine, getStackedAsPercentChartFormat, getVisibleSeriesIds, getXAxes, getXAxisColumns, getYAxes, getYAxisColumns, isConnectNullsEnabled, isCustomCalendarTimeSeries, isDateColumnChart, isDescendingSort, isResponsiveLayoutAuto, isResponsiveLayoutAutoOn, isResponsiveLayoutEnabled, isResponsiveLayoutNull, isSeriesPresentInVisibleSeries, isTimeSeries, isXAxisMeasure, isXAxisOrdinalBased, isYAxisOpposite, isYAxisOrdinalBased, isYAxisStackedAsPercent, } from '../core/chart-model-utils';
import * as chartTypeSpecificationService from '../core/chart-type-specification-service';
import { configOptions } from '../core/chart-type-specification-service';
import { getHighChartMetric } from '../core/conditional-formatting-utils';
import { columnHasInfinityValues, columnHasMinusInfinityValues, getAllColumnsConfig, getAugmentYaxisRawValue, getInfinityPlaceholder, getMaxXValue, getMaxYValue, getMetricsForSampledData, getMinXValue, getMinYValue, getQueryBasedYAxisColumns, getQueryToColumnMap, getSamplingRatio, getSeries, getTooltipConfig, getTotalRowCount, getTotalRowsDisplayed, getXAxisCardinality, getXAxisLabelAt, getXAxisRawValues, getYAxisRawValues, hasCaptionValuesForXAxis, hasCaptionValuesForYAxis, } from '../data/data-util';
import { getEnvFlags } from '../env';
import { offGridline, onGridline, responsiveLayoutDisabledSpacingBottom, } from '../theme/chart-theme-service';
import { AxisTypes, ChartResponsiveLayout, ChartType, } from '../types';
import { chartLevelAdvancedSettings, getColorPalette, getConditionalFormattingGradientColors, getConditionalFormattingGradientRange, getIncompleteThresholdForCustomCalendar, getMinTickIntervalForXAxis, getScreenReaderData, getTitleText, hasSharedYAxisColumnsBetweenQueries, onClickedSelectedPoint, onRightClickPointSelect, shouldDisableScrollbar, updateSeriesWithMarkerSettings, } from './blink-highchart-config-util';
import { getScrollEnabledMinMaxConfig } from './highchart-config.util';
import { getAxisTemplate } from './highchart-axis-renderer';
import { configureConditionalFormatting, configureSeriesVisibility, } from './highchart-series-util';
import { getFontByScreenSize, getScreenSizeClass, } from './highchart-theme-screen-util';
const incompleteZoneDashStyle = 'ShortDot';
const constants = {
    GROUPING_POINT_COUNT_THRESHOLD_HIGH: 1500,
    ACCESSIBILITY_FORMATTING_THRESHOLD: 1000,
    formatting: {
        MIN_AXIS_LABEL_GAP: 40,
        MAX_AXIS_LABEL_HEIGHT: 60,
        X_AXIS_TITLE_MIN_OFFSET: 40,
        MIN_X_AXIS_TITLE_OFFSET: 15,
        MIN_Y_AXIS_TITLE_OFFSET: 5,
        RIGHT_MARGIN_FOR_LEGEND: 0,
        BOTTOM_MARGIN_FOR_LEGEND: 0,
        TICK_SIZE: 10,
        PLOT_LINE_WIDTH: 1,
        PLOT_LINE_Z_INDEX: 4,
        INVERT_AXIS_MARGIN: 70,
        LEGEND_WIDTH: 150,
    },
};
export var GridLineDashStyle;
(function (GridLineDashStyle) {
    GridLineDashStyle["Dash"] = "Dash";
    GridLineDashStyle["DashDot"] = "DashDot";
    GridLineDashStyle["Dot"] = "Dot";
    GridLineDashStyle["LongDash"] = "LongDash";
    GridLineDashStyle["LongDashDot"] = "LongDashDot";
    GridLineDashStyle["LongDashDotDot"] = "LongDashDotDot";
    GridLineDashStyle["ShortDash"] = "ShortDash";
    GridLineDashStyle["ShortDashDot"] = "ShortDashDot";
    GridLineDashStyle["ShortDashDotDot"] = "ShortDashDotDot";
    GridLineDashStyle["ShortDot"] = "ShortDot";
    GridLineDashStyle["Solid"] = "Solid";
})(GridLineDashStyle || (GridLineDashStyle = {}));
const logger = create('blink-highchart-config');
function getHighchartChartType(chartType) {
    const highchartsType = chartTypeSpecificationService.getHighchartsType(chartType);
    return highchartsType.toLowerCase();
}
class BlinkHighchartConfig {
    constructor(chartModel, dataModel, config, env) {
        var _a;
        this.isSeriesColorUpgradedNeeded = false;
        this.disableColorRotation = false;
        this.configureDataLabels = () => {
            const allowOverlap = !isCustomLabelProcessingDisabled();
            getSeries(this.dataModel).forEach(serie => {
                serie.dataLabels = {
                    enabled: false,
                    allowOverlap,
                };
            });
            this._highchartConfig.yAxis.forEach(config => {
                config.stackLabels = config.stackLabels || {};
                config.stackLabels.enabled = false;
            });
            return this;
        };
        this.configureConnectNulls = () => {
            getSeries(this.dataModel).forEach(serie => {
                if (this.env.flags.isHandleMissingValuesEnabled) {
                    serie.handleMissingValues = getHandleMissingValues(this._chartModel, serie.valueColumnIdentifier);
                    serie.connectNulls = isConnectNullsEnabled(this._chartModel, serie.valueColumnIdentifier);
                }
                else {
                    serie.connectNulls = true;
                }
            });
            return this;
        };
        const chartType = getChartType(chartModel);
        this._chartModel = chartModel;
        this.dataModel = dataModel;
        this.env = env;
        this._containerDimensions = config.containerDimensions;
        this._shouldShowXAxisLabels = config.shouldShowXAxisLabels;
        this._shouldShowYAxisLabels = config.shouldShowYAxisLabels;
        this._shouldShowXAxisName = config.shouldShowXAxisName;
        this._shouldShowYAxisName = config.shouldShowYAxisName;
        this.disableColorRotation = config.disableColorRotation;
        this.isDataSourceDefault = config.isDataSourceDefault;
        this.hideAxisIcon = config.hideAxisIcon;
        this._configOptions = chartTypeSpecificationService.configOptions(chartType, chartModel);
        this._highchartConfig = {
            chart: {
                type: getHighchartChartType(chartType),
                props: this.getHighChartProperties(),
            },
            legend: {},
            navigator: {},
            plotOptions: {},
            rangeSelector: {},
            scrollbar: {},
            series: {},
            title: false,
            tooltip: {},
            xAxis: {},
            yAxis: {},
            shouldShowXAxisLabels: true,
            shouldShowYAxisLabels: true,
            shouldShowDataLabels: config.shouldShowDataLabels,
            dataLabelsEnabledByDefault: this._configOptions
                .dataLabelsEnabledByDefault,
            dataLabelsAlwaysEnabled: this._configOptions
                .dataLabelsAlwaysEnabled,
            isHandleMissingValuesEnabled: (_a = env.flags) === null || _a === void 0 ? void 0 : _a.isHandleMissingValuesEnabled,
            shouldRenderInStackedLabelFormat: this.shouldRenderInStackedLabelFormat(),
            allowLegendWithMultipleY: this._configOptions
                .allowLegendWithMultipleY,
        };
        this._disableTransformations = config.disableTransformations;
    }
    shouldRenderInStackedLabelFormat() {
        return this._configOptions.allowedConfigurations.showDetailedLabels;
    }
    shouldShowRegressionLine() {
        return (getShowRegressionLine(this._chartModel) &&
            canShowRegressionLine(this._chartModel) &&
            getSeries(this.dataModel).length === 1);
    }
    setContainer($chartContainer) {
        this._highchartConfig.chart.renderTo = $chartContainer;
        return this;
    }
    setColors() {
        const { colors, isColorUpdatedNeeded } = getColorPalette(this._chartModel, this.dataModel, this.env.flags.disableRandomColor || this.disableColorRotation, this.env.getTranslatedText);
        this.isSeriesColorUpgradedNeeded = isColorUpdatedNeeded;
        this._highchartConfig.colors = colors;
        return this;
    }
    disableZoom() {
        this._highchartConfig.chart.zoomType = '';
        this._highchartConfig.chart.panning = true;
        return this;
    }
    setZoomType() {
        this._highchartConfig.chart.zoomType = 'xy';
        return this;
    }
    getIncompleteThreshold(chartModel, seriesIndex) {
        const incompleteZone = this._configOptions.incompleteZone;
        if (incompleteZone.disabled) {
            return undefined;
        }
        const xAxisColumns = getXAxisColumns(chartModel);
        if (!isCalendarGregorianOrFiscal(xAxisColumns[0]) &&
            isCustomCalendarTimeSeries(chartModel)) {
            return getIncompleteThresholdForCustomCalendar(this.dataModel, chartModel, Date.now(), seriesIndex);
        }
        const getDefaultIncompleteThreshold = dateUtil.getTimeBucketStart.bind(this, Date.now());
        const timeBucket = getTimeBucket(xAxisColumns[0]);
        return incompleteZone.getIncompleteThreshold
            ? incompleteZone.getIncompleteThreshold(timeBucket)
            : getDefaultIncompleteThreshold(timeBucket);
    }
    configureAccessibility() {
        const numberOfDataPoints = this.dataModel.getDataArray()[0].getData()
            .length;
        this._configOptions.highcharts.accessibility = {
            point: {
                valueDescriptionFormat: '{point.y}.',
            },
        };
        if (isAccessibilityWithFormattingEnabled()) {
            if (numberOfDataPoints <=
                constants.ACCESSIBILITY_FORMATTING_THRESHOLD) {
                this._configOptions.highcharts.accessibility = {
                    point: {
                        descriptionFormatter(point) {
                            return getScreenReaderData(point.getLabelConfig());
                        },
                    },
                    series: {
                        descriptionFormat: null,
                    },
                };
            }
            else {
                logger.warn(`Formatting for screen reader data is disabled for performance reasons. Number of points is ${numberOfDataPoints}, exceeds the threshold of ${constants.ACCESSIBILITY_FORMATTING_THRESHOLD}.`);
            }
        }
        return this;
    }
    configureXAxis(gridLineEnabled) {
        var _a;
        const chartModel = this._chartModel;
        const isXAxisVertical = chartTypeSpecificationService.isXAxisVertical(getChartType(chartModel));
        const xAxis = this._highchartConfig.xAxis;
        xAxis.allowDecimals = true;
        this.configureGridline(xAxis, gridLineEnabled);
        let omitYear;
        if (isXAxisOrdinalBased(chartModel)) {
            xAxis.min = 0;
            if (this._configOptions.cardinalityIndexStartsFromZero) {
                xAxis.max = getXAxisCardinality(this.dataModel);
            }
            else {
                xAxis.max = getXAxisCardinality(this.dataModel) - 1;
            }
            xAxis.minTickInterval = 1;
            xAxis.minRange = 1;
            xAxis.type = 'category';
            xAxis.allowDecimals = false;
        }
        else if (isTimeSeries(chartModel)) {
            xAxis.minTickInterval = getMinTickIntervalForXAxis(chartModel);
            xAxis.type = 'datetime';
            if (isCustomCalendar()) {
                xAxis.tickPositioner = function () {
                    return getDateTickPositioner.call(this);
                };
            }
        }
        const queryDefinitions = this.dataModel.getQueryDefinitions();
        const xAxisColumn = queryDefinitions[0].xAxisColumns[0];
        if (!xAxis.title) {
            xAxis.title = {};
        }
        const axisId = (_a = getXAxes(chartModel)[0]) === null || _a === void 0 ? void 0 : _a.id;
        let axisSuffix = '';
        const dataModel = this.dataModel;
        if (isTimeSeries(chartModel) && !isXAxisOrdinalBased(chartModel)) {
            const max = dataModel.getMaxYValue(getQueryIndex(xAxisColumn), getDataRowIndex(xAxisColumn));
            const min = getMinXValue(chartModel, dataModel, xAxisColumn);
            const format = 'yyyy';
            const minYear = dateUtil.formatDate(min, format);
            const maxYear = dateUtil.formatDate(max, format);
            omitYear = minYear === maxYear;
            if (omitYear) {
                axisSuffix = `(${this.env.getTranslatedText('FOR')} ${minYear})`;
                xAxis.title.style = {
                    textAlign: 'center',
                };
            }
            xAxis.reversed = isXAxisVertical
                ? !isDescendingSort(chartModel, xAxisColumn)
                : isDescendingSort(chartModel, xAxisColumn);
        }
        xAxis.title.useHTML = true;
        xAxis.title.text = getAxisTemplate(axisId, chartModel, getXAxisColumns(chartModel), isXAxisVertical, this._disableTransformations, false, this.env.getTranslatedText, this.env.flags.allowMobileOverride, axisSuffix);
        xAxis.title.alwaysShownText = axisSuffix;
        if (!xAxis.labels) {
            xAxis.labels = {};
        }
        let showInfinity = false;
        let showMinusInfinity = false;
        let minXValue = Number.NEGATIVE_INFINITY;
        let maxXValue = Number.POSITIVE_INFINITY;
        let infinityPlaceholderValue = Number.POSITIVE_INFINITY;
        if (isXAxisMeasure(chartModel)) {
            xAxis.reversed = isXAxisVertical
                ? !isDescendingSort(chartModel, xAxisColumn)
                : isDescendingSort(chartModel, xAxisColumn);
            showInfinity = columnHasInfinityValues(this.dataModel, xAxisColumn);
            showMinusInfinity = columnHasMinusInfinityValues(this.dataModel, xAxisColumn);
            if (showInfinity || showMinusInfinity) {
                minXValue = getMinXValue(chartModel, dataModel, xAxisColumn);
                maxXValue = getMaxXValue(chartModel, dataModel, xAxisColumn);
                infinityPlaceholderValue = computeAxisInfinityPlaceholderValue(minXValue, maxXValue);
                xAxis.endOnTick = true;
                if (showInfinity) {
                    xAxis.max = infinityPlaceholderValue;
                }
                if (showMinusInfinity) {
                    xAxis.min = -1 * infinityPlaceholderValue;
                }
            }
            if (!isDoubleColumn(xAxisColumn)) {
                xAxis.allowDecimals = false;
            }
            xAxis.showInfinity = showInfinity;
            xAxis.showMinusInfinity = showMinusInfinity;
            xAxis.infinityPlaceholderValue = infinityPlaceholderValue;
            xAxis.tickPositioner = function () {
                return getUniqueTickPositionsForAxis.call(this, 'X');
            };
        }
        const xAxisColumnId = getId(xAxisColumn);
        xAxis.visible = this._shouldShowXAxisName;
        xAxis.labels.enabled = this._shouldShowXAxisLabels;
        xAxis.id = axisId || xAxisColumnId;
        xAxis.labels.useHTML = hasCaptionValuesForXAxis(this.dataModel);
        xAxis.valueColumn = xAxisColumnId;
        xAxis.labels.formatter = function () {
            return xAxisLabelFormatter.call(this);
        };
        if (this._configOptions.getMinTickIntervalForXAxis) {
            xAxis.minTickInterval = this._configOptions.getMinTickIntervalForXAxis(getXAxisCardinality(dataModel));
        }
        this._highchartConfig.cachedXAxisHtml = [xAxis.title.text];
        return this;
    }
    setAfterSetExtremesCallback(callback) {
        this._highchartConfig.xAxis.events = {
            afterSetExtremes: callback,
        };
        _.each(this._highchartConfig.yAxis, axis => {
            axis.events = {
                afterSetExtremes: callback,
            };
        });
        return this;
    }
    configureColorAxis() {
        var _a, _b;
        if (!this._configOptions.allowedConfigurations.showColorAxis) {
            return this;
        }
        const queryDefinition = this.dataModel.getQueryDefinitions()[0];
        const valueColumn = this._configOptions
            .colorAxisColumn
            ? _.find(queryDefinition.getColumnsInDataOrder(), col => getId(col) === getId(this._configOptions.colorAxisColumn))
            : null;
        if (!valueColumn) {
            this._highchartConfig.colorAxis =
                this._highchartConfig.colorAxis || {};
            this._highchartConfig.colorAxis.showInLegend = false;
            return this;
        }
        if (this._highchartConfig.colorAxis) {
            delete this._highchartConfig.colorAxis.showInLegend;
        }
        const gradientColors = getConditionalFormattingGradientColors(this._chartModel, valueColumn.id);
        const colorStops = gradientColors ||
            getColorScaleForSerie(this._chartModel, this.dataModel);
        const showInfinity = columnHasInfinityValues(this.dataModel, valueColumn);
        const showMinusInfinity = columnHasMinusInfinityValues(this.dataModel, valueColumn);
        let gradientRange = getConditionalFormattingGradientRange(this._chartModel, valueColumn.id);
        if (gradientColors && gradientColors.length === 2 && gradientRange) {
            gradientRange = [
                gradientRange[0],
                gradientRange[gradientRange.length - 1],
            ];
        }
        const dataMinYValue = getMinYValue(this.dataModel, valueColumn);
        const dataMaxYValue = getMaxYValue(this.dataModel, valueColumn);
        const appliedMinYValue = (_a = gradientRange === null || gradientRange === void 0 ? void 0 : gradientRange[0]) !== null && _a !== void 0 ? _a : dataMinYValue;
        const appliedMaxYValue = (_b = gradientRange === null || gradientRange === void 0 ? void 0 : gradientRange[gradientRange.length - 1]) !== null && _b !== void 0 ? _b : dataMaxYValue;
        const infinityPlaceholderValue = getInfinityPlaceholder(this.dataModel, valueColumn);
        let stops;
        if (gradientRange === null && colorStops.length === 3) {
            stops = [0, 0.5, 1].map((pos, i) => [
                pos,
                colorStops[i],
            ]);
        }
        else {
            stops = colorStops.map((color, i) => {
                let gradientPosition = _.isNil(gradientRange === null || gradientRange === void 0 ? void 0 : gradientRange[i])
                    ? i
                    : (gradientRange[i] - appliedMinYValue) /
                        (appliedMaxYValue - appliedMinYValue);
                if (Number.isNaN(gradientPosition)) {
                    gradientPosition = 0;
                }
                if (gradientPosition === Infinity) {
                    gradientPosition = 1;
                }
                return [gradientPosition, color];
            });
        }
        const colorAxis = {
            stops,
            labels: {
                overflow: false,
                formatter() {
                    return colorAxisFormatter.call(this);
                },
                style: {
                    fontSize: getFontByScreenSize(getScreenSizeClass(false)),
                },
            },
            tickPositioner() {
                return getTickPositionColorAxis.call(this);
            },
            colorAxisColumn: valueColumn.id,
            min: appliedMinYValue,
            max: appliedMaxYValue,
        };
        if (showInfinity) {
            colorAxis.max = infinityPlaceholderValue;
        }
        else if (showMinusInfinity) {
            colorAxis.min = -1 * infinityPlaceholderValue;
        }
        this._highchartConfig.colorAxis = colorAxis;
        return this;
    }
    configureYAxis(gridlineEnabled) {
        const chartModel = this._chartModel;
        this._highchartConfig.yAxis = [];
        this._highchartConfig.cachedYAxisHtml = [];
        const maxHeight = 210;
        const yAxes = getYAxes(chartModel);
        _.each(yAxes, (yAxis, idx) => {
            const columnsInAxis = yAxis.properties.linkedColumns.map(colId => getColumn(chartModel, colId));
            const yAxisConfig = this.prepareYAxisConfig(columnsInAxis, yAxis.id, gridlineEnabled, isYAxisOpposite(yAxis, idx));
            this._highchartConfig.yAxis.push(yAxisConfig);
        });
        if (getChartType(chartModel) !== ChartType.Treemap) {
            this._highchartConfig.responsive = {
                rules: [
                    {
                        condition: {
                            maxHeight,
                        },
                        chartOptions: {
                            yAxis: yAxes.map(() => ({
                                tickPositioner: () => {
                                    return null;
                                },
                            })),
                        },
                    },
                ],
            };
        }
        _.each(this._highchartConfig.yAxis, axis => {
            var _a;
            return _.merge(axis, {
                startOnTick: true,
                endOnTick: true,
                ...(_a = configOptions(getChartType(chartModel), chartModel)
                    .highcharts) === null || _a === void 0 ? void 0 : _a.yAxis,
            });
        });
        return this;
    }
    configureAxisScroll(enableMobileOverride) {
        if (!this._configOptions.allowedConfigurations.advanced.display
            .responsiveLayout) {
            return this;
        }
        if (enableMobileOverride) {
            this._responsiveLayoutPreference = ChartResponsiveLayout.AutoOn;
            return this;
        }
        let responsiveLayoutEnabled = isResponsiveLayoutEnabled(this._chartModel);
        const minMaxConfig = getScrollEnabledMinMaxConfig(getSeries(this.dataModel), getChartType(this._chartModel), this._containerDimensions.width, this._containerDimensions.height, this._chartModel);
        if (isResponsiveLayoutAuto(this._chartModel) ||
            isResponsiveLayoutNull(this._chartModel)) {
            const responsiveLayoutDefault = this.env.flags
                .responsiveLayoutDefault;
            if (!_.isUndefined(responsiveLayoutDefault)) {
                responsiveLayoutEnabled = responsiveLayoutDefault;
            }
            else if (isDateColumnChart(this._chartModel)) {
                responsiveLayoutEnabled = true;
            }
            else if (isResponsiveLayoutNull(this._chartModel) ||
                !isResponsiveLayoutAutoOn(this._chartModel)) {
                if (minMaxConfig.max - minMaxConfig.min + 1 >=
                    getXAxisCardinality(this.dataModel)) {
                    responsiveLayoutEnabled = true;
                }
                else {
                    responsiveLayoutEnabled = false;
                }
            }
            if (responsiveLayoutEnabled) {
                this._responsiveLayoutPreference = ChartResponsiveLayout.AutoOn;
            }
            else {
                this._responsiveLayoutPreference =
                    ChartResponsiveLayout.AutoOff;
            }
        }
        if (responsiveLayoutEnabled === false) {
            const scrollBarConfig = this.getScrollBarEnabledConfig();
            _.assign(this._highchartConfig.xAxis, scrollBarConfig);
            if (scrollBarConfig.scrollbar.enabled) {
                this._highchartConfig.chart.spacingBottom = responsiveLayoutDisabledSpacingBottom;
            }
        }
        return this;
    }
    configurePlotLines() {
        const chartModel = this._chartModel;
        const chartConfig = this._highchartConfig;
        const axes = getAxes(chartModel);
        const axisConfigs = [chartConfig.xAxis, ...chartConfig.yAxis];
        axes.forEach((axis) => {
            const axisColumnIds = axis.properties.linkedColumns;
            if (axis.properties.axisType === AxisTypes.X &&
                !isXAxisMeasure(chartModel)) {
                return;
            }
            const axisChartConfig = _.find(axisConfigs, config => {
                return config.id === axis.id;
            });
            if (!axisChartConfig) {
                return;
            }
            if (!axisChartConfig.plotLines) {
                axisChartConfig.plotLines = [];
                axisChartConfig.plotBands = [];
            }
            const axisColumnMetrics = _.reduce(axisColumnIds, (metrics, columnId) => {
                const conditionalFormattings = getAllConditionalFormattingRulesForPlotlines(chartModel, columnId);
                if (!conditionalFormattings.length) {
                    return metrics;
                }
                const columnMetrics = _.map(conditionalFormattings, (conditionalFormatting) => getHighChartMetric(conditionalFormatting, chartModel.inScopeParameters));
                return [...metrics, ...columnMetrics];
            }, []);
            const metrics = axisColumnMetrics.slice(0);
            axisChartConfig.plotLines = _.flatMap(_.reverse(_.filter(metrics, metric => !metric.plotAsBand)), metric => {
                return [metric.max, metric.min].map(threshold => ({
                    color: metric.color,
                    width: constants.formatting.PLOT_LINE_WIDTH,
                    value: threshold,
                    zIndex: constants.formatting.PLOT_LINE_Z_INDEX,
                }));
            });
            axisChartConfig.plotBands = _.reverse(metrics
                .filter(metric => metric.plotAsBand)
                .map(function (metric) {
                return {
                    color: metric.color,
                    from: metric.min,
                    to: metric.max,
                };
            }));
        });
        return this;
    }
    configureChartOptions(onChartAreaClick) {
        if (!this._highchartConfig.chart.events)
            this._highchartConfig.chart.events = {};
        this._highchartConfig.chart.events.click = evt => {
            onChartAreaClick(evt);
        };
        return this;
    }
    configureSeries(onSeriesClick, onSeriesRightClick, onSeriesDoubleClick, onSeriesMouseOver, onSeriesMouseOut, numEnabledSeriesOnLoad, onLegendClick, disableHighlightOnHover) {
        const dataModel = this.dataModel;
        const getTranslatedText = this.env.getTranslatedText;
        const chartModel = this._chartModel;
        this.setYAxisInfoInSeries();
        const series = getSeries(this.dataModel);
        if (!series) {
            return this;
        }
        const seriesPlotOptions = (this._highchartConfig.plotOptions.series = {
            stickyTracking: false,
        });
        const getDefaultIncompleteColor = function (serie) {
            return Highcharts.Color(serie.color)
                .setOpacity(0.4)
                .get();
        };
        const isCustomCalendarTimeSeriesChart = isCustomCalendarTimeSeries(chartModel);
        const isTimeSeriesChart = isTimeSeries(chartModel);
        const isXAxisOrdinalBasedChart = isXAxisOrdinalBased(chartModel);
        if (isCustomCalendarTimeSeriesChart ||
            (isTimeSeriesChart && !isXAxisOrdinalBasedChart)) {
            this._configOptions.incompleteZone =
                this._configOptions.incompleteZone || {};
            if (!this._configOptions.incompleteZone.disabled) {
                series.forEach((serie, idx) => {
                    const incompleteThresholdValue = this.getIncompleteThreshold(chartModel, idx);
                    serie.zones = [
                        {
                            value: incompleteThresholdValue,
                        },
                        {
                            dashStyle: incompleteZoneDashStyle,
                            getColor: this._configOptions.incompleteZone
                                .getIncompleteColor ||
                                getDefaultIncompleteColor,
                        },
                    ];
                    serie.data.forEach(serieDataPoint => {
                        if (serieDataPoint.x > incompleteThresholdValue) {
                            serieDataPoint.ignoreInRegression = true;
                        }
                    });
                });
            }
        }
        chartTypeSpecificationService.seriesOverride(getChartType(chartModel), chartModel, this.dataModel);
        let visibleSeriesIds = getVisibleSeriesIds(chartModel);
        if (!isSeriesPresentInVisibleSeries(chartModel, this.dataModel)) {
            visibleSeriesIds = [];
        }
        if (visibleSeriesIds && visibleSeriesIds.length) {
            this._highchartConfig.series = getSeries(this.dataModel).slice();
            this._highchartConfig.series = configureSeriesVisibility(chartModel, dataModel, this._highchartConfig.series);
        }
        if (!this._highchartConfig.series ||
            !this._highchartConfig.series.length) {
            this._highchartConfig.series = getSeries(this.dataModel).slice(0, numEnabledSeriesOnLoad);
            getSeries(this.dataModel)
                .slice(numEnabledSeriesOnLoad, getSeries(this.dataModel).length)
                .forEach((s, idx) => {
                s.color = this._highchartConfig.colors[idx % this._highchartConfig.colors.length];
                s.visible = false;
            }, this);
            const displayYValuesWithLegendSeries = this._configOptions
                .displayYValuesWithLegendSeries;
            if (displayYValuesWithLegendSeries) {
                const chartModelSeries = getSeries(this.dataModel);
                const totalSeries = chartModelSeries.length;
                if (totalSeries >= numEnabledSeriesOnLoad) {
                    const yAxisColumnsAsSeries = getYAxisColumns(chartModel).slice(1);
                    const totalYAxisAsSeries = yAxisColumnsAsSeries.length;
                    const legendSeries = chartModelSeries.slice(0, totalSeries - totalYAxisAsSeries);
                    const yAxisSeries = chartModelSeries.slice(totalSeries - totalYAxisAsSeries);
                    this._highchartConfig.series = legendSeries.slice(0, numEnabledSeriesOnLoad);
                    Array.prototype.push.apply(this._highchartConfig.series, yAxisSeries);
                }
            }
        }
        const seriesColors = getSeriesColorsMap(chartModel);
        this._highchartConfig.series.forEach((serie, idx) => {
            const serieColorBySeriesId = getColorBySerieName(seriesColors, serie.blinkSeriesId);
            const serieColorBySeriesName = getColorBySerieName(seriesColors, serie.name);
            const hasCustomColorSelectorArray = this._configOptions
                .allowedConfigurations.useCustomColorSelector;
            if (hasCustomColorSelectorArray) {
            }
            else if (this._configOptions.isMultiColorSeries) {
                serie.data.forEach((point, index) => {
                    const xAxisLabel = getXAxisLabelAt(chartModel, this.dataModel, index, null, null, this.env.getTranslatedText);
                    point.name = xAxisLabel;
                    const serieColorByXAxisLabel = getColorBySerieName(seriesColors, xAxisLabel);
                    if (serieColorByXAxisLabel) {
                        point.color = serieColorByXAxisLabel;
                    }
                    else {
                        point.color = this._highchartConfig.colors[index % this._highchartConfig.colors.length];
                    }
                });
            }
            else if (serieColorBySeriesId) {
                serie.color = serieColorBySeriesId;
            }
            else if (serieColorBySeriesName) {
                serie.color = serieColorBySeriesName;
            }
            else if (!serie.color) {
                serie.color = this._highchartConfig.colors[idx % this._highchartConfig.colors.length];
            }
            delete serie.regression;
            if (this.shouldShowRegressionLine()) {
                serie.regression = true;
                serie.regressionSettings = {
                    visible: serie.visible === void 0 || serie.visible,
                };
            }
            serie.id = serie.name;
        });
        seriesPlotOptions.dataLabels = {
            enabled: false,
            padding: 2,
            borderRadius: 2,
            style: {
                fontSize: '12px',
            },
            formatter() {
                const props = this.series.chart.userOptions.chart.props;
                const customFormatter = chartTypeToDatalabelFormatter[props.chartType];
                if (customFormatter) {
                    return customFormatter.call(this, chartModel, dataModel, getTranslatedText);
                }
                return dataLabelFormatter.call(this);
            },
        };
        let groupingEnabled = true;
        const chartConfig = this._highchartConfig;
        configureConditionalFormatting(chartModel, chartConfig);
        if (this._configOptions.highThresholdGrouping) {
            const maxPointsInAnySeries = _.max(getSeries(this.dataModel).map(function (serie) {
                return (serie.data && serie.data.length) || 0;
            }));
            if (maxPointsInAnySeries <=
                constants.GROUPING_POINT_COUNT_THRESHOLD_HIGH) {
                groupingEnabled = false;
            }
        }
        if (this._configOptions.minPointLengthSupported) {
            seriesPlotOptions.minPointLength = chartTypeSpecificationService.getMinBarHeight(getEnvFlags().enableMinimumBarHeight);
        }
        seriesPlotOptions.turboThreshold = 1000000;
        seriesPlotOptions.events = {
            contextmenu(evt) {
                const clickSelf = this;
                if (onRightClickPointSelect(clickSelf.options)) {
                    const clickPoint = onClickedSelectedPoint(this.points, evt.point.index);
                    const isAccumulateClick = evt.ctrlKey || evt.metaKey || evt.shiftKey;
                    if (!clickPoint.selected || isAccumulateClick) {
                        clickPoint.select(true, isAccumulateClick);
                    }
                    else if (!getEnvFlags().isContextMenuEnabledOnRightClick) {
                        clickPoint.select(false);
                    }
                }
                onSeriesRightClick(evt, clickSelf.color, clickSelf.name);
            },
            dblclick(evt) {
                const clickSelf = this;
                onSeriesDoubleClick(evt, clickSelf.color, clickSelf.name);
            },
            click(evt) {
                const clickSelf = this;
                const isAccumulateClick = evt.ctrlKey || evt.metaKey || evt.shiftKey;
                if (getEnvFlags().isContextMenuEnabledOnLeftClick &&
                    onRightClickPointSelect(clickSelf.options)) {
                    const clickPoint = onClickedSelectedPoint(this.points, evt.point.index);
                    if (!clickPoint.selected || isAccumulateClick) {
                        clickPoint.select(true, isAccumulateClick);
                    }
                }
                onSeriesClick(evt, clickSelf.color, clickSelf.name);
                if (getEnvFlags().isContextMenuEnabledOnLeftClick &&
                    onRightClickPointSelect(clickSelf.options)) {
                    setTimeout(() => {
                        const clickPoint = onClickedSelectedPoint(this.points, evt.point.index);
                        if (!clickPoint.selected || isAccumulateClick) {
                            clickPoint.select(true, isAccumulateClick);
                        }
                    }, 10);
                }
            },
            mouseOver(evt) {
                const clickSelf = this;
                onSeriesMouseOver(evt, clickSelf.color, clickSelf.name);
            },
            mouseOut(evt) {
                const clickSelf = this;
                onSeriesMouseOut(evt, clickSelf.color, clickSelf.name);
            },
        };
        const isWaterfallChart = getChartType(chartModel) === ChartType.Waterfall;
        if (isWaterfallChart) {
            chartConfig.series.forEach((serie, serieIdx) => {
                const color = serie.color
                    ? serie.color
                    : chartConfig.colors[serieIdx >= chartConfig.colors.length
                        ? serieIdx - chartConfig.colors.length
                        : serieIdx];
                serie.zoneAxis = 'y';
                serie.color = color;
                serie.negativeColor = chroma(color)
                    .alpha(0.3)
                    .css();
            });
        }
        if (getChartType(this._chartModel) === ChartType.Pareto) {
            this._configOptions.highcharts.paretoColor = getColorBySerieName(getSeriesColorsMap(this._chartModel), PARETO_ID);
        }
        if (disableHighlightOnHover) {
            _.merge(this._highchartConfig.plotOptions.series, {
                states: {
                    hover: {
                        enabled: false,
                    },
                },
                cursor: 'default',
            });
        }
        addCortexBoundSeries(chartModel, this);
        return this;
    }
    configureSerieGetter(getter) {
        this._highchartConfig._blinkSerieGetter = getter;
        return this;
    }
    configureChartTitle(shouldShowTotalRows = false, shouldShowHighchartTitle = false) {
        let sampledMetrics;
        if (shouldShowTotalRows) {
            sampledMetrics = {
                totalRowCount: getTotalRowCount(this._chartModel),
                totalRowsDisplayed: getTotalRowsDisplayed(this._chartModel),
            };
        }
        else {
            sampledMetrics = getMetricsForSampledData(this._chartModel, this.env.flags.getMaxDataSizeForHighCardinalityCharts, this.env.flags.dataBatchSize);
        }
        const { dataBatchSize } = this.env.flags;
        const batchSize = getBatchSize(this._chartModel, dataBatchSize, getChartType(this._chartModel));
        const samplingRatio = getSamplingRatio(this._chartModel);
        const titleText = getTitleText(samplingRatio, sampledMetrics, this.isDataSourceDefault, batchSize, this.env.getTranslatedText);
        this._highchartConfig.title = {
            text: titleText,
            align: 'left',
            verticalAlign: 'bottom',
            x: 10,
            y: 0,
            style: {
                color: 'var(--ts-var-axis-data-label-color, #777E8B)',
                fontFamily: 'var(--ts-var-axis-data-label-font-family, Optimo-Plain)',
                fontSize: '12px',
                display: shouldShowHighchartTitle ? 'block' : 'none',
            },
        };
        return this;
    }
    configureOverrides() {
        _.merge(this._highchartConfig, this._configOptions.highcharts);
    }
    configureDefaults() {
        return this;
    }
    configureLegend(handler, enabled, hasLegend, legendColorHandler) {
        const verticalAlign = 'middle';
        const layout = 'vertical';
        const align = 'right';
        const y = blinkConstants.legendTopSpacing.VERTICAL;
        this._highchartConfig.legend = {
            enabled: hasLegend && enabled && !this._configOptions.doNotShowLegend,
            dblClick(item) {
                const serieName = item.name;
                handler(serieName);
            },
            symbolClick(item, anchor) {
                legendColorHandler(item, anchor);
            },
        };
        return this;
    }
    configureWindowing() {
        const navigator = this._highchartConfig.navigator;
        navigator.enabled = false;
        const scrollbar = this._highchartConfig.scrollbar;
        scrollbar.enabled = false;
        this._highchartConfig.rangeSelector = {
            enabled: false,
            inputEnabled: false,
        };
        return this;
    }
    isTooltipDisabled() {
        var _a, _b, _c;
        const tooltipConfig = getTooltipConfig(this._chartModel);
        const isColumnsListEmpty = ((_a = tooltipConfig === null || tooltipConfig === void 0 ? void 0 : tooltipConfig.columnIds) === null || _a === void 0 ? void 0 : _a.length) === 0;
        const isFeatureEnabled = isTooltipCustomizationEnabled();
        if (isFeatureEnabled && isColumnsListEmpty) {
            return true;
        }
        if ((_b = this.env.flags) === null || _b === void 0 ? void 0 : _b.isChartSettingsV2Enabled) {
            const advancedProperties = getHighchartCustomisationSettings(this._chartModel);
            const tooltipEnabledFromV2 = (_c = advancedProperties === null || advancedProperties === void 0 ? void 0 : advancedProperties.tooltip) === null || _c === void 0 ? void 0 : _c.enabled;
            if (tooltipEnabledFromV2 === false) {
                return true;
            }
        }
        return false;
    }
    configureGenericTooltip() {
        const tooltip = this._highchartConfig.tooltip;
        tooltip.valueDecimals = 2;
        tooltip.shared = false;
        tooltip.useHTML = true;
        tooltip.crosshairs = false;
        tooltip.hideDelay = blinkConstants.CHART_TOOLTIP_DELAY;
        tooltip.backgroundColor = null;
        tooltip.borderWidth = 0;
        tooltip.snap = 10;
        tooltip.followPointer = true;
        tooltip.outside = true;
        tooltip.delayForDisplay = 500;
        if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
            tooltip.positioner = function (labelWidth, labelHeight, point) {
                var _a;
                const chartCord = this.chart.container.getBoundingClientRect();
                const x = chartCord.left + point.plotX + this.chart.plotLeft;
                const y = chartCord.top + point.plotY + this.chart.plotTop;
                const additionGap = ((_a = this.chart.hoverPoint) === null || _a === void 0 ? void 0 : _a.barX) ? 10 : 20;
                return {
                    x: x - labelWidth / 2 - additionGap,
                    y: y - labelHeight - additionGap,
                };
            };
        }
        tooltip.enable =
            !this.env.flags.disableChartTooltips && !this.isTooltipDisabled();
        tooltip.formatter = function () {
            const tooltipTemplate = toolTipFormatter.call(this);
            if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
                window.tooltipTemplate = `<div class='highcharts-tooltip'>${tooltipTemplate}</div>`;
            }
            return tooltipTemplate;
        };
        return this;
    }
    configureLineOptions() {
        this._highchartConfig.plotOptions.line = {};
        const series = getSeries(this.dataModel);
        if (!series) {
            return this;
        }
        updateSeriesWithMarkerSettings(series);
        return this;
    }
    configureExporting() {
        this._highchartConfig.exporting = {
            enabled: false,
        };
        return this;
    }
    configureAnimation() {
        if (!this._highchartConfig.plotOptions) {
            this._highchartConfig.plotOptions = {};
        }
        if (!this._highchartConfig.plotOptions.series) {
            this._highchartConfig.plotOptions.series = {};
        }
        this._highchartConfig.plotOptions.series.animation = {
            duration: 1500,
        };
        return this;
    }
    configureRenderCallback(onRender) {
        if (_.isFunction(onRender)) {
            this._highchartConfig.chart.events =
                this._highchartConfig.chart.events || {};
            this._highchartConfig.chart.events.render = onRender;
        }
        return this;
    }
    build() {
        this._highchartConfig.chart.reflow = false;
        return this._highchartConfig;
    }
    toString() {
        const replacerFn = getCircularReplacer().bind(this);
        return JSON.stringify(this, replacerFn, 2);
    }
    getHighChartProperties() {
        const chartModel = this._chartModel;
        const dataModel = this.dataModel;
        return {
            chartType: getChartType(chartModel),
            allColumns: getAllColumnsConfig(chartModel, dataModel),
            axes: getAllAxesConfig(chartModel),
            xAxisColumns: getColumnIds(getXAxisColumns(chartModel)),
            yAxisColumns: getColumnIds(getYAxisColumns(chartModel)),
            legendColumns: getColumnIds(getLegendColumns(chartModel)),
            radialColumn: getColumnId(getRadialColumn(chartModel)),
            categoryColumns: getColumnIds(getCategoryColumns(chartModel)),
            xValueToRawValues: getXAxisRawValues(dataModel),
            yValueToRawValues: getYAxisRawValues(dataModel),
            augmentYValueToRawValues: getAugmentYaxisRawValue(dataModel),
            isYAxisStackedAsPercent: isYAxisStackedAsPercent(chartModel) &&
                chartTypeSpecificationService.isStackedChart(getChartType(chartModel)),
            isYAxisOrdinalBased: isYAxisOrdinalBased(chartModel),
            isXAxisOrdinalBased: isXAxisOrdinalBased(chartModel),
            isTimeSeries: isTimeSeries(chartModel),
            queryToColumnMap: getQueryToColumnMap(dataModel),
            stackedChartFormat: getStackedAsPercentChartFormat(chartModel),
            tooltipConfig: getTooltipConfig(chartModel),
        };
    }
    setYAxisInfoInSeries() {
        const chartModel = this._chartModel;
        const yAxes = getYAxes(chartModel);
        getSeries(this.dataModel).forEach(serie => {
            const columnId = serie.valueColumnIdentifier;
            const axisForColumn = getAxisForColumn(chartModel, columnId);
            if (axisForColumn) {
                const axisId = axisForColumn.id;
                const axisIndex = _.findIndex(yAxes, axis => axis.id === axisId);
                serie.yAxis = axisIndex;
            }
        });
    }
    prepareYAxisConfig(axisColumns, axisId, gridlineEnabled, isOpposite) {
        const chartModel = this._chartModel;
        const isYAxisVertical = !chartTypeSpecificationService.isXAxisVertical(getChartType(chartModel));
        if (hasSharedYAxisColumnsBetweenQueries(this.dataModel.getQueryDefinitions())) {
            logger.warn('y Axis Columns is shared between multiple queries. this will break.');
        }
        const allowAxisCustomization = configOptions(getChartType(chartModel), chartModel).allowedConfigurations.allowAxisCustomization;
        const yAxisColumns = getQueryBasedYAxisColumns(this.dataModel);
        const maxY = _.max(yAxisColumns.map(col => getMaxYValue(this.dataModel, col)));
        const minY = _.min(yAxisColumns.map(col => getMinYValue(this.dataModel, col)));
        const showInfinity = yAxisColumns.some(col => columnHasInfinityValues(this.dataModel, col));
        const showMinusInfinity = yAxisColumns.some(col => columnHasMinusInfinityValues(this.dataModel, col));
        const infinityPlaceholderValue = computeAxisInfinityPlaceholderValue(minY, maxY);
        const chartConfigOptions = this._configOptions;
        const allowOverlap = !isCustomLabelProcessingDisabled();
        const yAxisConfig = {
            id: axisId,
            title: {
                text: getAxisTemplate(axisId, chartModel, axisColumns, isYAxisVertical, this._disableTransformations, isOpposite, this.env.getTranslatedText, this.env.flags.allowMobileOverride),
                useHTML: true,
                style: {
                    textOverflow: 'ellipsis',
                },
            },
            labels: {
                enabled: this._shouldShowYAxisLabels,
                formatter() {
                    return yAxisLabelFormatter.call(this);
                },
            },
            tickPositioner: allowAxisCustomization
                ? function tickPositioner() {
                    return getUniqueTickPositionsForAxis.call(this, 'Y', chartConfigOptions);
                }
                : undefined,
            stackLabels: {
                enabled: false,
                allowOverlap,
                useHTML: true,
                crop: false,
                overflow: 'allow',
                style: {
                    textOverflow: 'ellipsis',
                },
                formatter() {
                    return stackLabelFormatter.call(this);
                },
            },
            opposite: isOpposite,
            allowDecimals: axisColumns.some((col) => {
                return isDoubleColumn(col) || isFloat(col);
            }),
            name: getAxisName(chartModel, axisId, this.env.getTranslatedText),
            showInfinity,
            showMinusInfinity,
            infinityPlaceholderValue,
            visible: this._shouldShowYAxisName,
        };
        if (showInfinity) {
            yAxisConfig.max = infinityPlaceholderValue;
        }
        if (showMinusInfinity) {
            yAxisConfig.min = -1 * infinityPlaceholderValue;
        }
        this.configureGridline(yAxisConfig, gridlineEnabled);
        if (hasCaptionValuesForYAxis(this.dataModel, getDataRowIndex(yAxisColumns[0]))) {
            yAxisConfig.labels.useHTML = true;
        }
        this._highchartConfig.cachedYAxisHtml.push(yAxisConfig.title.text);
        if (axisColumns.length === 1) {
            this.setSingleColumnedYAxisProperties(yAxisConfig, axisColumns[0], chartModel, showMinusInfinity, minY);
        }
        return yAxisConfig;
    }
    setSingleColumnedYAxisProperties(axisConfig, column, chartModel, showMinusInfinity, minYValue) {
        if ((isDateColumn(column) || isTimeColumn(column)) &&
            !isYAxisOrdinalBased(chartModel)) {
            const timeBucket = getTimeBucket(column);
            axisConfig.tickInterval = dateUtil.getIntervalMillisForTimeBucket(timeBucket);
        }
        else if (isEffectivelyNonNumeric(column)) {
            axisConfig.tickInterval = 1;
        }
        if (!showMinusInfinity && minYValue > 0) {
            if (isDateFamilyColumn(column) ||
                this._configOptions.useDefaultMinTick) {
                axisConfig.min = undefined;
            }
            else {
                axisConfig.min = 0;
            }
        }
        axisConfig.stackLabels.style = {
            fontFamily: 'Optimo-Plain',
            fontSize: '12px',
            fontWeight: 'normal',
        };
    }
    configureGridline(axis, hasGridLine, overrides) {
        const onGridLineWidth = !_.isNil(overrides === null || overrides === void 0 ? void 0 : overrides.gridLineWidth)
            ? _.toNumber(overrides === null || overrides === void 0 ? void 0 : overrides.gridLineWidth)
            : onGridline.gridLineWidth;
        axis.gridLineEnabled = hasGridLine;
        axis.gridLineWidth = hasGridLine
            ? onGridLineWidth
            : offGridline.gridLineWidth;
        axis.gridLineCustomWidth = overrides === null || overrides === void 0 ? void 0 : overrides.gridLineWidth;
        axis.gridLineColor =
            (overrides === null || overrides === void 0 ? void 0 : overrides.gridLineColor) || onGridline.gridLineColor;
        axis.gridLineDashStyle =
            (overrides === null || overrides === void 0 ? void 0 : overrides.gridLineDashStyle) || onGridline.gridLineDashStyle;
        return axis;
    }
    getScrollBarEnabledConfig() {
        const chartModel = this._chartModel;
        const minMaxConfig = getScrollEnabledMinMaxConfig(getSeries(this.dataModel), getChartType(chartModel), this._containerDimensions.width, this._containerDimensions.height, chartModel);
        const scrollbarConfig = {
            scrollbar: {
                enabled: true,
                showFull: true,
            },
        };
        if (shouldDisableScrollbar(chartModel, getSeries(this.dataModel), minMaxConfig, this.dataModel)) {
            scrollbarConfig.scrollbar = {
                enabled: false,
                showFull: false,
            };
        }
        return _.assign(scrollbarConfig, minMaxConfig);
    }
    configureHighchartCustomisationSettings() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        const MARKER_TYPE_MAP = {
            Circle: 'circle',
            Square: 'square',
            Diamond: 'diamond',
            Triangle: 'triangle',
            'Triangle-down': 'triangle-down',
        };
        try {
            const chartModel = this._chartModel;
            const chartType = getChartType(chartModel);
            const isXAxisVerticalType = chartTypeSpecificationService.isXAxisVertical(chartType);
            const options = configOptions(chartType, chartModel);
            const advancedProperties = getHighchartCustomisationSettings(chartModel);
            logger.debug(advancedProperties);
            if (!advancedProperties ||
                !options.allowedConfigurations.allowChartCustomisation)
                return;
            if (advancedProperties.tooltip) {
                const highchartAdvProps = this._highchartConfig.chart.props
                    .advancedProperties;
                this._highchartConfig.chart.props.advancedProperties = {
                    ...(highchartAdvProps ? { ...highchartAdvProps } : {}),
                    tooltip: advancedProperties.tooltip,
                };
            }
            if (advancedProperties.gridlines) {
                const xAxisGridlineProps = isXAxisVerticalType
                    ? advancedProperties.gridlines.yAxis
                    : advancedProperties.gridlines.xAxis;
                if (xAxisGridlineProps) {
                    const xAxis = this._highchartConfig.xAxis;
                    const isGridlineEnabled = _.isNil(xAxisGridlineProps.enabled)
                        ? xAxis.gridLineEnabled
                        : xAxisGridlineProps.enabled;
                    this.configureGridline(xAxis, isGridlineEnabled, xAxisGridlineProps);
                }
                const yAxisGridlineProps = isXAxisVerticalType
                    ? advancedProperties.gridlines.xAxis
                    : advancedProperties.gridlines.yAxis;
                if (yAxisGridlineProps) {
                    _.each(this._highchartConfig.yAxis, axis => {
                        const isGridlineEnabled = _.isNil(yAxisGridlineProps.enabled)
                            ? axis.gridLineEnabled
                            : yAxisGridlineProps.enabled;
                        return _.merge(axis, {
                            ...this.configureGridline(axis, isGridlineEnabled, yAxisGridlineProps),
                        });
                    });
                }
            }
            if (advancedProperties.regression) {
                this._highchartConfig.series.forEach((serie, index) => {
                    if (serie.regression && serie.regressionSettings) {
                        const customRegressionOverrides = advancedProperties.regression;
                        serie.regressionSettings = {
                            ...serie.regressionSettings,
                            ...{
                                name: customRegressionOverrides.name,
                                dataLabels: customRegressionOverrides.dataLabels,
                                color: customRegressionOverrides.color,
                                lineWidth: _.toNumber(customRegressionOverrides.lineWidth),
                                linetype: customRegressionOverrides.linetype,
                                dashStyle: customRegressionOverrides.dashStyle,
                            },
                        };
                    }
                });
            }
            if (advancedProperties.answerLevelSettings) {
                const { backgroundColor, borderColor, borderWidth, plotBackgroundColor, dashStyle, plotBackgroundImage, connectingLines: hideConnectingLines, lineWidth, markerRadius, markerType, padding, paddingCategories, width, chart, plotArea, lineColor, } = chartLevelAdvancedSettings(getChartType(this._chartModel), advancedProperties.answerLevelSettings);
                {
                    const plotOptionOverrides = {
                        ...(!_.isNil(width) && isValidNumericString(width)
                            ? { pointWidth: _.toNumber(width) }
                            : {}),
                        ...(!_.isNil(padding) && isValidNumericString(padding)
                            ? { pointPadding: _.toNumber(padding) }
                            : {}),
                        ...(!_.isNil(paddingCategories) &&
                            isValidNumericString(paddingCategories)
                            ? { groupPadding: _.toNumber(paddingCategories) }
                            : {}),
                        ...(!_.isNil(hideConnectingLines) && hideConnectingLines
                            ? { lineColor: 'transparent' }
                            : {}),
                        ...(!_.isNil(markerType) && !_.isEmpty(markerType)
                            ? {
                                marker: {
                                    ...(((_a = this._highchartConfig.plotOptions
                                        .series) === null || _a === void 0 ? void 0 : _a.marker) || {}),
                                    symbol: MARKER_TYPE_MAP[markerType],
                                },
                            }
                            : {}),
                    };
                    this._highchartConfig.plotOptions.series = {
                        ...this._highchartConfig.plotOptions.series,
                        ...plotOptionOverrides,
                    };
                    this._highchartConfig.plotOptions.scatter = {
                        ...this._highchartConfig.plotOptions.scatter,
                        ...(!_.isNil(markerRadius) &&
                            isValidNumericString(markerRadius)
                            ? {
                                marker: {
                                    ...(((_b = this._highchartConfig.plotOptions
                                        .scatter) === null || _b === void 0 ? void 0 : _b.marker) || {}),
                                    radius: _.toNumber(markerRadius),
                                },
                            }
                            : {}),
                    };
                    this._highchartConfig.plotOptions.pie = {
                        ...this._highchartConfig.plotOptions.pie,
                        ...(!_.isNil(borderColor) && !_.isEmpty(borderColor)
                            ? { borderColor }
                            : {}),
                        ...(!_.isNil(borderWidth) &&
                            isValidNumericString(borderWidth)
                            ? { borderWidth: _.toNumber(borderWidth) }
                            : {}),
                    };
                    this._highchartConfig.plotOptions.treemap = {
                        ...this._highchartConfig.plotOptions.treemap,
                        ...(!_.isNil(borderWidth) &&
                            isValidNumericString(borderWidth)
                            ? { borderWidth: _.toNumber(borderWidth) }
                            : {}),
                    };
                    this._highchartConfig.plotOptions.heatmap = {
                        ...this._highchartConfig.plotOptions.heatmap,
                        ...(!_.isNil(borderWidth) &&
                            isValidNumericString(borderWidth)
                            ? { borderWidth: _.toNumber(borderWidth) }
                            : {}),
                    };
                    this._highchartConfig.plotOptions.line = {
                        ...this._highchartConfig.plotOptions.line,
                        ...(!_.isNil(lineWidth) &&
                            isValidNumericString(lineWidth)
                            ? { lineWidth: _.toNumber(lineWidth) }
                            : {}),
                        ...(!_.isNil(dashStyle) && !_.isEmpty(dashStyle)
                            ? { dashStyle }
                            : {}),
                    };
                }
                {
                    const chartOverrides = {
                        ...(!_.isNil(backgroundColor ||
                            ((_c = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _c === void 0 ? void 0 : _c.backgroundColor)) &&
                            !_.isEmpty(backgroundColor ||
                                ((_d = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _d === void 0 ? void 0 : _d.backgroundColor))
                            ? {
                                backgroundColor: ((_e = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _e === void 0 ? void 0 : _e.backgroundColor) ||
                                    backgroundColor,
                            }
                            : {}),
                        ...(!_.isNil((_f = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _f === void 0 ? void 0 : _f.borderColor) &&
                            !_.isEmpty((_g = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _g === void 0 ? void 0 : _g.borderColor)
                            ? {
                                borderColor: (_h = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _h === void 0 ? void 0 : _h.borderColor,
                            }
                            : {}),
                        ...(!_.isNil((_j = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _j === void 0 ? void 0 : _j.borderWidth)
                            ? {
                                borderWidth: (_k = chart === null || chart === void 0 ? void 0 : chart.backgroundStyle) === null || _k === void 0 ? void 0 : _k.borderWidth,
                            }
                            : {}),
                        ...(!_.isNil(plotBackgroundColor ||
                            ((_l = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _l === void 0 ? void 0 : _l.backgroundColor)) &&
                            !_.isEmpty(plotBackgroundColor ||
                                ((_m = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _m === void 0 ? void 0 : _m.backgroundColor))
                            ? {
                                plotBackgroundColor: ((_o = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _o === void 0 ? void 0 : _o.backgroundColor) ||
                                    plotBackgroundColor,
                            }
                            : {}),
                        ...(!_.isNil((_p = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _p === void 0 ? void 0 : _p.borderColor) &&
                            !_.isEmpty((_q = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _q === void 0 ? void 0 : _q.borderColor)
                            ? {
                                plotBorderColor: (_r = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _r === void 0 ? void 0 : _r.borderColor,
                            }
                            : {}),
                        ...(!_.isNil((_s = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _s === void 0 ? void 0 : _s.borderWidth)
                            ? {
                                plotBorderWidth: (_t = plotArea === null || plotArea === void 0 ? void 0 : plotArea.backgroundStyle) === null || _t === void 0 ? void 0 : _t.borderWidth,
                            }
                            : {}),
                        ...(!_.isNil(plotBackgroundImage) &&
                            !_.isEmpty(plotBackgroundImage)
                            ? { plotBackgroundImage }
                            : {}),
                    };
                    this._highchartConfig.chart = {
                        ...this._highchartConfig.chart,
                        ...chartOverrides,
                    };
                }
                if (!_.isEmpty(lineColor) && isValidHexColor(lineColor)) {
                    this._highchartConfig.paretoColor = lineColor;
                }
            }
            if (advancedProperties.stackLabelStyles) {
                const chartType = getChartType(chartModel);
                const chartConfigOptions = configOptions(chartType, chartModel);
                if (!chartConfigOptions.allowedConfigurations.showStackLabels) {
                    return;
                }
                const stackLabelStyles = advancedProperties.stackLabelStyles;
                const { backgroundColor, borderColor, borderWidth, maxCharacters, style, } = stackLabelStyles;
                const { color, fontSize, boldFontWeight, underlineTextDecoration, italicFontStyle, } = style || {};
                _.each(this._highchartConfig.yAxis, axis => {
                    if (axis.stackLabels) {
                        const existingStyle = axis.stackLabels.style || {};
                        axis.stackLabels = {
                            ...axis.stackLabels,
                            ...(backgroundColor && !_.isEmpty(backgroundColor)
                                ? { backgroundColor }
                                : {}),
                            ...(borderColor && !_.isEmpty(borderColor)
                                ? { borderColor }
                                : {}),
                            ...(borderWidth && !_.isEmpty(borderWidth)
                                ? { borderWidth }
                                : {}),
                            ...(maxCharacters &&
                                !_.isEmpty(maxCharacters) &&
                                !Number.isNaN(maxCharacters)
                                ? { maxCharacters: _.toNumber(maxCharacters) }
                                : {}),
                            style: {
                                ...existingStyle,
                                ...(!_.isEmpty(color) ? { color } : {}),
                                ...(!_.isEmpty(fontSize) ? { fontSize } : {}),
                                ...(boldFontWeight
                                    ? { fontWeight: 'bold' }
                                    : {}),
                                ...(underlineTextDecoration
                                    ? { textDecoration: 'underline' }
                                    : {}),
                                ...(italicFontStyle
                                    ? { fontStyle: 'italic' }
                                    : {}),
                            },
                        };
                    }
                });
            }
        }
        catch (e) {
            logger.error(e, 'Error in configureHighchartCustomisationSettings');
        }
    }
    processAxisSettings(axis) {
        var _a, _b;
        const advancedXAxisSettings = getAxisAdvancedPropertiesById(this._chartModel.vizProp, axis.id);
        logger.debug({ advancedXAxisSettings });
        if (advancedXAxisSettings) {
            const { axisSettings, labels, ticks } = advancedXAxisSettings;
            const { axisName, visible } = axisSettings || {};
            const { isAxisNameVisible, opposite, plotLines } = axisName || {};
            const customPlotLines = _.isEmpty(plotLines)
                ? axis.plotLines
                : plotLines;
            const hideAxisComponents = visible === false;
            const obj = {
                ...(hideAxisComponents
                    ? {
                        lineWidth: 0,
                        tickWidth: 0,
                        minorTickWidth: 0,
                    }
                    : {}),
                plotLines: _.isArray(customPlotLines)
                    ? customPlotLines
                    : [
                        !_.isEmpty(customPlotLines === null || customPlotLines === void 0 ? void 0 : customPlotLines.value)
                            ? customPlotLines
                            : {},
                    ],
                ...(hideAxisComponents || isAxisNameVisible === false
                    ? { title: { text: '' } }
                    : {}),
                ...(hideAxisComponents ? { labels: { enabled: false } } : {}),
            };
            const chartConfigOptions = configOptions(getChartType(this._chartModel), this._chartModel);
            const isXYAxisTransposed = chartConfigOptions.allowedConfigurations.transposeXY;
            const baseAxes = isXYAxisTransposed
                ? getYAxes(this._chartModel)
                : getXAxes(this._chartModel);
            const fallbackStyleSettings = (_b = getAxisAdvancedPropertiesById(this._chartModel.vizProp, (_a = baseAxes[0]) === null || _a === void 0 ? void 0 : _a.id)) === null || _b === void 0 ? void 0 : _b.labels;
            logger.debug({ advancedXAxisSettings });
            const labelStyleSettings = labels && Object.keys(labels).length > 2
                ? labels
                : fallbackStyleSettings;
            if (labels) {
                const { enabled, maxCharacters } = labels;
                const { boldFontWeight, color, italicFontStyle, fontSize, underlineTextDecoration, wrap, } = labelStyleSettings;
                if (axis.labels)
                    obj.labels = {
                        style: {
                            ...(!_.isEmpty(color) ? { color } : {}),
                            ...(!_.isEmpty(fontSize) ? { fontSize } : {}),
                            ...(boldFontWeight ? { fontWeight: 'bold' } : {}),
                            ...(underlineTextDecoration
                                ? { textDecoration: 'underline' }
                                : {}),
                            ...(italicFontStyle ? { fontStyle: 'italic' } : {}),
                            ...(wrap === false ? { whiteSpace: 'nowrap' } : {}),
                        },
                        ...(!_.isEmpty(maxCharacters) ? { maxCharacters } : {}),
                        ...(!_.isNil(enabled)
                            ? { enabled: hideAxisComponents ? false : enabled }
                            : {}),
                    };
            }
            if (ticks && ticks.showTicks) {
                const { tickColor, tickLength, tickWidth, tickInterval, tickPositions, } = ticks;
                _.merge(obj, {
                    tickColor,
                    tickLength: tickLength ? Number(tickLength) : undefined,
                    tickWidth,
                    tickInterval,
                });
            }
            _.merge(axis, obj);
        }
    }
    configAdvancedAxisSettings() {
        try {
            if (_.isArray(this._highchartConfig.xAxis)) {
                this._highchartConfig.xAxis.forEach(axis => this.processAxisSettings(axis));
            }
            else {
                this.processAxisSettings(this._highchartConfig.xAxis);
            }
            if (_.isArray(this._highchartConfig.yAxis)) {
                this._highchartConfig.yAxis.forEach(axis => this.processAxisSettings(axis));
            }
            else {
                this.processAxisSettings(this._highchartConfig.yAxis);
            }
        }
        catch (e) {
            logger.error(e, 'Error in configAdvancedAxisSettings');
        }
    }
    configAdvancedColumnSettings() {
        const FILTER_OPERATOR_MAP = {
            'Less than': '<',
            'Greater than': '>',
            'Less than or equal to': '<=',
            'Greater than or equal to': '>=',
            'Equal to': '===',
        };
        try {
            this.dataModel.series.forEach(serie => {
                var _a;
                const columnAdvancedProperties = getColumnAdvancedPropertiesById(this._chartModel.vizProp, serie.valueColumnIdentifier);
                logger.debug('columnAdvancedProperties', columnAdvancedProperties, serie.valueColumnIdentifier);
                if (columnAdvancedProperties) {
                    const { dataLabels, enableMarkers, lineStyle, markerStyle, } = columnAdvancedProperties;
                    const { align, backgroundColor, borderRadius, borderWidth, borderColor, inside, filter, maxCharacters, style, } = dataLabels || {};
                    const { color, fontSize, width, boldFontWeight, underlineTextDecoration, italicFontStyle, } = style || {};
                    const existingStyle = ((_a = serie.dataLabels) === null || _a === void 0 ? void 0 : _a.style) || {};
                    const chartType = getChartType(this._chartModel);
                    const getFilterProperty = () => {
                        const { isYAxisStackedAsPercent, } = this._highchartConfig.chart.props;
                        if (isYAxisStackedAsPercent) {
                            return 'percentage';
                        }
                        if (chartType === ChartType.Heatmap) {
                            return 'value';
                        }
                        return 'y';
                    };
                    const filterProperty = getFilterProperty();
                    const obj = {
                        dataLabels: {
                            ...serie.dataLabels,
                            ...(filter && (filter === null || filter === void 0 ? void 0 : filter.operator)
                                ? {
                                    filter: {
                                        property: filterProperty,
                                        value: filter.value,
                                        operator: FILTER_OPERATOR_MAP[filter.operator],
                                    },
                                }
                                : {}),
                            align: _.isEmpty(align)
                                ? existingStyle.align
                                : align,
                            backgroundColor: _.isEmpty(backgroundColor)
                                ? existingStyle.backgroundColor
                                : backgroundColor,
                            borderRadius: _.isEmpty(borderRadius)
                                ? existingStyle.borderRadius
                                : borderRadius,
                            borderWidth: _.isEmpty(borderWidth)
                                ? existingStyle.borderWidth
                                : borderWidth,
                            borderColor: _.isEmpty(borderColor)
                                ? existingStyle.borderColor
                                : borderColor,
                            maxCharacters: _.isEmpty(maxCharacters) ||
                                Number.isNaN(maxCharacters)
                                ? existingStyle.maxCharacters
                                : _.toNumber(maxCharacters),
                            style: {
                                ...(existingStyle || {}),
                                ...(!_.isEmpty(color) ? { color } : {}),
                                ...(!_.isEmpty(fontSize) ? { fontSize } : {}),
                                ...(boldFontWeight
                                    ? { fontWeight: 'bold' }
                                    : {}),
                                ...(underlineTextDecoration
                                    ? { textDecoration: 'underline' }
                                    : {}),
                                ...(italicFontStyle
                                    ? { fontStyle: 'italic' }
                                    : {}),
                                ...(!_.isEmpty(width) ? { width } : {}),
                            },
                        },
                        marker: {
                            ...(_.isNil(enableMarkers)
                                ? {}
                                : { enabled: enableMarkers }),
                            ...(_.isNil(markerStyle === null || markerStyle === void 0 ? void 0 : markerStyle.type)
                                ? {}
                                : { symbol: markerStyle === null || markerStyle === void 0 ? void 0 : markerStyle.type }),
                            ...(_.isNil(markerStyle === null || markerStyle === void 0 ? void 0 : markerStyle.size)
                                ? {}
                                : { radius: markerStyle === null || markerStyle === void 0 ? void 0 : markerStyle.size }),
                        },
                        ...(_.isNil(lineStyle === null || lineStyle === void 0 ? void 0 : lineStyle.thickness)
                            ? {}
                            : { lineWidth: lineStyle === null || lineStyle === void 0 ? void 0 : lineStyle.thickness }),
                        ...(_.isNil(lineStyle === null || lineStyle === void 0 ? void 0 : lineStyle.dashStyle)
                            ? {}
                            : { dashStyle: lineStyle === null || lineStyle === void 0 ? void 0 : lineStyle.dashStyle }),
                    };
                    _.merge(serie, obj);
                }
            });
        }
        catch (e) {
            logger.error(e, 'Error in configAdvancedColumnSettings');
        }
    }
    get chartDataModel() {
        return this.dataModel;
    }
}
export { BlinkHighchartConfig };
//# sourceMappingURL=blink-highchart-config.js.map